/**
 * The four parts of Wend and the two foundations they stand on — what each one
 * is, what it receives, and what it hands on. Shared by the interactive loop
 * (`sections/Loop.tsx`) and the "Follow one person" stepper beside it, so the
 * picture, the panel and the story cannot name a part two ways.
 *
 * ## WHY SIX BOXES, AS THE CONCEPT DREW THEM
 *
 * The concept's loop (`wend-concept/src/components/blueprint/pillars.js`) has
 * Reach, Connect, Sense and Flow on top and two foundations underneath — one
 * person per human, and the audiences built over them. Slices 67–70 built both
 * foundations for real: every uploaded column is a field, a rule segment
 * refreshes itself, and one person is resolved across uploads, Reach leads and
 * replies. So the site draws the same six boxes. The foundations are named the
 * way the product names them — **People** and **Audiences** — not the
 * concept's "Wend Core", which is not a word the application uses anywhere.
 *
 * ## EVERY "RECEIVES" AND "HANDS ON" LINE IS SHIPPED BEHAVIOUR
 *
 * The concept's lists were a design, and several described things that do not
 * exist. Each was checked against the code before it was kept, and these were
 * dropped, deliberately:
 *
 *   - **Sense → Reach, "pause · move budget".** Sense reads no ad data and has
 *     no action that touches an ad. Every start, pause and budget change in
 *     Reach is a person's.
 *   - **Flow → Reach, "converted people as an ad exclusion list".** Not built.
 *     Meta's custom-audience permission is not held, and nothing leaves Wend
 *     for an ad platform but the ad itself.
 *   - **Lead forms that ask a birthday.** Meta Lead Ads' own form fetch is not
 *     built (slice 70 shipped click-to-WhatsApp leads and a CSV import of
 *     Meta's lead export). A click-to-WhatsApp lead carries the person's number
 *     and the ad they came from; that is all the Reach lines claim.
 *   - **SMS.** Connect is email and WhatsApp.
 *
 * Reach therefore receives nothing — and the panel says so in a sentence
 * rather than drawing an empty list, because "nothing changes an ad on its
 * own" is itself the honest fact a buyer asks about.
 *
 * One line was ADDED that the concept did not have: **Flow → Connect**. Every
 * flow message goes out through Connect's send path — the same guard, opt-out
 * check, credits and transport as a campaign (`campaign-send.runner.ts`) —
 * and there is no second road to a transport. Drawing it says what a buyer's
 * compliance person will ask: "do journeys skip the checks?" They do not.
 *
 * `scripts/verify-site-copy.tsx` reads the rendered text of all of this.
 */

/** The three hands-on tabs under the loop (sections/Loop.tsx). */
export type TryTab = 'segment' | 'flow' | 'story';

export type PartId = 'reach' | 'people' | 'audiences' | 'connect' | 'sense' | 'flow';

/** `part`: one of the four parts. `foundation`: underneath every part. */
export type PartTone = 'part' | 'foundation';

export interface PartFlowLine {
  /** Another part's id, or plain words for something outside the six (an upload, Acme's shop). */
  readonly other: PartId | string;
  readonly what: string;
}

export interface Part {
  readonly id: PartId;
  readonly name: string;
  readonly glyph: string;
  /**
   * For the four parts, "Part 01" to "Part 04" (the order the page's sections
   * number them); for a foundation, the word "Foundation". Never a status: the
   * owner asked for the "Available" tags to go (Oct 2026).
   */
  readonly kicker: string;
  readonly tone: PartTone;
  /** One line under the name in the box. */
  readonly line: string;
  readonly does: string;
  readonly receives: readonly PartFlowLine[];
  /** Said instead of the list when `receives` is empty. */
  readonly receivesNothing?: string;
  readonly hands: readonly PartFlowLine[];
  /** The "See it work" tab that shows the part working, if there is one. */
  readonly tryIt?: { readonly tab: TryTab; readonly label: string };
}

/** Reach's one requirement, said once as a plain note under its section's copy (Pillars.tsx). Not a status tag. */

export const PARTS: Readonly<Record<PartId, Part>> = {
  reach: {
    id: 'reach',
    name: 'Reach',
    glyph: '◇',
    kicker: 'Part 01',
    tone: 'part',
    line: 'Ads on your own Meta ad account',
    does: 'Ads published paused to your own Meta ad account. A tap on a click-to-WhatsApp ad becomes a lead.',
    receives: [],
    receivesNothing: 'Nothing. No part of Wend changes an ad on its own.',
    hands: [
      { other: 'people', what: 'New leads, with the ad they came from' },
      { other: 'flow', what: 'A “new lead” event to start on' },
    ],
  },
  people: {
    id: 'people',
    name: 'People',
    glyph: '☰',
    kicker: 'Foundation',
    tone: 'foundation',
    line: 'One person, every source',
    does: 'Uploads, leads and replies land on one person. Every column becomes a field.',
    receives: [
      { other: 'your uploads', what: 'Any columns you keep' },
      { other: 'reach', what: 'New leads' },
      { other: 'connect', what: 'Replies and opt-outs' },
    ],
    hands: [{ other: 'audiences', what: 'Every field, for rules' }],
    tryIt: { tab: 'segment', label: 'Build a segment' },
  },
  audiences: {
    id: 'audiences',
    name: 'Audiences',
    glyph: '◫',
    kicker: 'Foundation',
    tone: 'foundation',
    line: 'Segments from rules',
    does: 'Rules over any field. A rule segment keeps itself current and records who entered and left.',
    receives: [{ other: 'people', what: 'Every field' }],
    hands: [
      { other: 'connect', what: 'A segment to send to' },
      { other: 'flow', what: '“Entered” and “left”' },
    ],
    tryIt: { tab: 'segment', label: 'Build a segment' },
  },
  connect: {
    id: 'connect',
    name: 'Connect',
    glyph: '◈',
    kicker: 'Part 02',
    tone: 'part',
    line: 'Messages from your own accounts',
    does: 'Sends to a segment through your own accounts, with delivery recorded per person. Every flow message goes through the same checks.',
    receives: [
      { other: 'audiences', what: 'A segment' },
      { other: 'flow', what: 'Each flow message' },
      { other: 'sense', what: 'Suggestions a person acts on' },
    ],
    hands: [
      { other: 'sense', what: 'Delivery, replies and outcomes' },
      { other: 'people', what: 'Replies and opt-outs' },
    ],
  },
  sense: {
    id: 'sense',
    name: 'Sense',
    glyph: '◆',
    kicker: 'Part 03',
    tone: 'part',
    line: 'What changed, and what to do',
    does: 'Reads campaigns and flows, says what changed, and drafts a flow when that is the fix. A draft does nothing until a person launches it.',
    receives: [
      { other: 'connect', what: 'Delivery and outcomes' },
      { other: 'flow', what: 'Step results and the holdout' },
    ],
    hands: [
      { other: 'flow', what: 'A draft flow; a person launches it' },
      { other: 'connect', what: 'Suggestions' },
    ],
  },
  flow: {
    id: 'flow',
    name: 'Flow',
    glyph: '⌥',
    kicker: 'Part 04',
    tone: 'part',
    line: 'Journeys that react',
    does: 'Starts on a segment, a schedule, a reply or a lead; waits, branches and stops at the goal. Always launched by a person.',
    receives: [
      { other: 'audiences', what: '“Entered” events' },
      { other: 'sense', what: 'Draft flows' },
      { other: 'reach', what: 'New leads' },
    ],
    hands: [
      { other: 'connect', what: 'Each message, same checks' },
      { other: 'sense', what: 'Step results' },
    ],
    tryIt: { tab: 'flow', label: 'Run a flow' },
  },
};

/** The order the boxes are listed in on a phone, and the order the stepper's rail uses. */
export const PART_ORDER: readonly PartId[] = ['reach', 'people', 'audiences', 'connect', 'sense', 'flow'];

/** The four parts a customer buys, in the product's sidebar order. */
export const FOUR_PARTS: readonly PartId[] = ['reach', 'connect', 'sense', 'flow'];

export function isPartId(value: string): value is PartId {
  return (PART_ORDER as readonly string[]).includes(value);
}

/** "Reach" for a part id; the words themselves for anything else ("your uploads"). */
export function nameOf(other: string): string {
  return isPartId(other) ? PARTS[other].name : other;
}
